import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

import { firstValueFrom } from 'rxjs';

import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';
import { SessionContextService } from 'src/app/core/services/session-context.service';
import {
    SnackbarBannerData
} from 'src/app/shared/components/snackbar-banner/models/snackbar-banner.model';
import {
    SnackbarBannerComponent
} from 'src/app/shared/components/snackbar-banner/snackbar-banner.component';

import {
    ConsentAcceptance, LegalAcknowledgement, LegalAcknowledgementService
} from '../services/legal-acknowledgement.service';
import { TranslateService } from '@ngx-translate/core';
import { TelemetryService } from 'src/app/core/telemetry/telemetry.service';

interface LegalAcknowledgementsState {
  acknowledgements: LegalAcknowledgement[];
  activeAcknowledgementDetails: LegalAcknowledgement | null;
  acknowledgementCache: Record<string, LegalAcknowledgement>;
  isSubmitting: boolean;
  isAcknowledgementLoading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: LegalAcknowledgementsState = {
  acknowledgements: [],
  activeAcknowledgementDetails: null,
  acknowledgementCache: {},
  isSubmitting: false,
  isAcknowledgementLoading: false,
  error: null,
  successMessage: null,
};

export const LegalAcknowledgementsStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      legalAcknowledgementService = inject(LegalAcknowledgementService),
      sessionContextService = inject(SessionContextService),
      snackBar = inject(MatSnackBar),
      translationService = inject(TranslateService),
      telemetry = inject(TelemetryService),
    ) => ({
        async loadAcknowledgementDetails(
          acknowledgementType: AcknowledgementType,
          forceReload = false,
        ): Promise<LegalAcknowledgement> {
          const cachedAcknowledgement =
            store.acknowledgementCache()[acknowledgementType];
          if (cachedAcknowledgement && !forceReload) {
            patchState(store, {
              activeAcknowledgementDetails: cachedAcknowledgement,
              isAcknowledgementLoading: false,
              error: null,
            });
            return cachedAcknowledgement;
          }

          patchState(store, { isAcknowledgementLoading: true, error: null });

          try {
            const { participantId, sessionId } = await sessionContextService.getContext();

            const acknowledgementDetails = await firstValueFrom(
              legalAcknowledgementService.getAcknowledgementDetails(
                acknowledgementType,
                participantId,
                sessionId,
              ),
            );

            if (!acknowledgementDetails) {
              throw new Error(
                `Legal acknowledgement '${acknowledgementType}' not found`,
              );
            }

            patchState(store, {
              activeAcknowledgementDetails: acknowledgementDetails,
              acknowledgementCache: {
                ...store.acknowledgementCache(),
                [acknowledgementType]: acknowledgementDetails,
              },
              isAcknowledgementLoading: false,
              error: null,
            });

            return acknowledgementDetails;
          } catch (error) {
            const errorMessage = translationService.instant('legal-acknowledgements.errors.LOAD_ACKNOWLEDGEMENT_FAILED');

            telemetry.trackException({
              exception: error as Error,
              severityLevel: SeverityLevel.Error,
            }, {
              component: 'LegalAcknowledgementsStore',
              method: 'loadAcknowledgementDetails',
              acknowledgementType,
            });

            patchState(store, {
              isAcknowledgementLoading: false,
              error: errorMessage,
            });

            snackBar.openFromComponent<
              SnackbarBannerComponent,
              SnackbarBannerData
            >(SnackbarBannerComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              data: {
                message: errorMessage,
                variant: 'warning',
              },
            });
            throw error;
          }
        },

        async acceptAcknowledgements(
          acknowledgementTypes: AcknowledgementType[],
        ): Promise<void> {
          patchState(store, { isSubmitting: true, error: null });

          try {
            const { participantId, sessionId } = await sessionContextService.getContext();

            const consents: ConsentAcceptance[] = acknowledgementTypes.map(type => {
              const cached = store.acknowledgementCache()[type];
              if (!cached) {
                throw new Error(`Consent for type '${type}' not loaded. Call loadAcknowledgementDetails first.`);
              }
              return {
                acknowledgementType: type,
                consentTextId: cached.id,
              };
            });

            const result = await firstValueFrom(
              legalAcknowledgementService.acceptAcknowledgements(
                participantId,
                sessionId,
                consents,
              ),
            );

            if (result.success) {
              patchState(store, {
                isSubmitting: false,
                successMessage: result.message,
                error: null,
              });
            } else {
              throw new Error(result.message || 'Submission failed');
            }
          } catch (error) {
            const errorMessage = translationService.instant('legal-acknowledgements.errors.SUBMIT_ACKNOWLEDGEMENTS_FAILED');

            telemetry.trackException({
              exception: error as Error,
              severityLevel: SeverityLevel.Error,
            }, {
              component: 'LegalAcknowledgementsStore',
              method: 'acceptAcknowledgements',
              acknowledgementTypes: acknowledgementTypes.join(', '),
            });

            patchState(store, {
              isSubmitting: false,
              error: errorMessage,
              successMessage: null,
            });

            snackBar.openFromComponent<
              SnackbarBannerComponent,
              SnackbarBannerData
            >(SnackbarBannerComponent, {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              data: {
                message: errorMessage,
                variant: 'warning',
              },
            });
            throw error;
          }
        },

        clearError() {
          patchState(store, { error: null });
        },
    }),
  ),
);
